import { CalendarMonthOutlined } from '@mui/icons-material';
import { Chip, Popover, Stack, Tooltip, Typography } from '@mui/material';
import { AssortmentTag } from '@retail/my-assortment/types';
import { useProductsTFunction } from '@retail/products/i18n';
import { SupplierItemAssortmentTag } from '@retail/products/types';
import { getLLDate } from '@retail/utils';
import { useState } from 'react';
import { TagDateFields } from './TagDateFields';

interface Props {
  tag: SupplierItemAssortmentTag;
  /** The sub-assortment itself, whose own validity period limits the dates on the item. */
  assortmentTag?: AssortmentTag;
  onChange: (tag: SupplierItemAssortmentTag) => void;
  onRemove: () => void;
}

/**
 * A tag chip that shows the period the item is in the sub-assortment and opens a date
 * editor on click, so every tag on every item can have its own dates.
 */
export function TagDateChip({ tag, assortmentTag, onChange, onRemove }: Props) {
  const t = useProductsTFunction();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const dateRangeLabel = `${getLLDate(tag.validFrom)} – ${
    tag.validTo ? getLLDate(tag.validTo) : t('products.mgItem.assortmentTags.untilFurtherNotice')
  }`;

  return (
    <>
      <Tooltip title={t('products.mgItem.assortmentTags.editDates')}>
        <Chip
          clickable
          size="small"
          variant="outlined"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          onDelete={onRemove}
          sx={{
            height: 'auto',
            py: 0.5,
            '& .MuiChip-label': { px: 1 },
            '& .MuiChip-deleteIcon': { alignSelf: 'flex-start', mt: 0.25 },
          }}
          label={
            <Stack>
              <Typography variant="subtitle2" lineHeight={1.2}>
                {tag.tagName ?? tag.tagId}
              </Typography>
              <Stack flexDirection="row" alignItems="center" gap={0.5} color="text.secondary">
                <CalendarMonthOutlined sx={{ fontSize: 14 }} />
                <Typography variant="body3" lineHeight={1.2}>
                  {dateRangeLabel}
                </Typography>
              </Stack>
            </Stack>
          }
        />
      </Tooltip>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack gap={2} p={3} maxWidth={440}>
          <Typography variant="h6">{tag.tagName ?? tag.tagId}</Typography>

          <TagDateFields tag={tag} assortmentTag={assortmentTag} onChange={onChange} />
        </Stack>
      </Popover>
    </>
  );
}
